import React, { useEffect, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import styles from './schedulePicker.module.scss';
import cx from 'classnames';

interface SchedulePickerProps {
  onClose: () => void;
  selectedDate: Date;
  setSelectedDate: (date: Date) => void;
  selectedTime: Date;
  setSelectedTime: (time: Date) => void;
}

export const SchedulePicker: React.FC<SchedulePickerProps> = ({ onClose, selectedDate, setSelectedDate, selectedTime, setSelectedTime }) => {

  const datePickerRef = useRef<DatePicker>(null);
  const timePickerRef = useRef<DatePicker>(null);

  const [isDatePickerOpen, setIsDatePickerOpen] = useState<boolean>(false);
  const [isTimePickerOpen, setIsTimePickerOpen] = useState<boolean>(false);

  const getMinTime = () => {
    
    const now = new Date();
    const minutes = now.getMinutes();
    const roundedMinutes = Math.ceil((minutes + 1));
    const minTime = new Date(now);
    minTime.setMinutes(roundedMinutes);
    minTime.setSeconds(0);
    minTime.setMilliseconds(0);
          
    // If rounded to next hour
      if (roundedMinutes >= 60) {
        minTime.setHours(minTime.getHours() + 1);
        minTime.setMinutes(0);
      }

      return minTime;
    }

  return (
    <div className={styles.schedulePickerContainer}>
        <div className={styles.datePicker}>
          <DatePicker
            ref={datePickerRef}
            selected={selectedDate}
            onChange={(date: Date | null) => {
              if (date) setSelectedDate(date);
              setIsDatePickerOpen(false);
            }}
            dateFormat="MMMM d, yyyy"
            minDate={new Date()}
            open={isDatePickerOpen}
            className={styles.datePickerInput}
            customInput={
              <CustomToggle 
                isOpen={isDatePickerOpen}
                onToggle={() => {
                  if(isDatePickerOpen){
                    setIsDatePickerOpen(false);
                  } else {
                    setIsDatePickerOpen(true);
                  }
                }}
              />
            }
          />
        </div>
        <div className={styles.timePicker}>
          <DatePicker
            ref={timePickerRef}
            selected={selectedTime}
            onChange={(time: Date | null) => {
              if (time) setSelectedTime(time);
              setIsTimePickerOpen(false);
            }}
            showTimeSelect
            showTimeSelectOnly
            timeIntervals={15}
            minTime={
              selectedDate.toDateString() === new Date().toDateString()
              ? getMinTime()
              : new Date(new Date().setHours(0, 0, 0, 0))
            }
            maxTime={new Date(new Date().setHours(23, 45, 0, 0))}
            dateFormat="h:mm aa"
            open={isTimePickerOpen}
            className={styles.timePickerInput}
            customInput={
              <CustomToggle 
              isOpen={isTimePickerOpen}
              onToggle={() => {
                  if(isTimePickerOpen){
                    setIsTimePickerOpen(false);
                  } else {
                    setIsTimePickerOpen(true);
                  }
                }}
              />
            }
          />
        </div>
        <i className={cx("paperflite-close", styles.closeIcon)} onClick={onClose} />
    </div>
  );
};

interface CustomInputProps {
  value?: string;
  onClick?: () => void;
  isOpen?: boolean;
  onToggle?: () => void;
}

const CustomToggle = React.forwardRef<HTMLDivElement, CustomInputProps>(
  ({ value, onClick, isOpen, onToggle }, ref) => {
    const handleClick = (e: React.MouseEvent) => {
      e.preventDefault();
      onToggle?.();
    };

    return (
      <div
        ref={ref}
        className={styles.datePickerWrapper}
        onClick={handleClick}
      >
        <span className={styles.dateDisplay}>
          {value || "Select a date"}
        </span>
        <button
          type="button"
          className={styles.dropdownButton}
          onClick={handleClick}
        >
          <i className={cx("paperflite-dropdown", styles.dropdownIcon, {
            [styles.pickerOpen]: isOpen
          })} />
        </button>
      </div>
    );
  }
);